import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import TextField from '@pelckmans/elementis/components/text-field';
import { useToast } from '@pelckmans/elementis/components/toast';
import { isAxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TOAST_ID } from '../../../../constants/ToastIds';
import ModalContext from '../../../../contexts/ModalContext';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import { License, startLicenseTransfer } from '../../api/licenses';

const BASE = 'PAGES.PROFILE.LICENSES.MY_LICENSES.TRANSFER_LICENSE_MODAL';

type FormValues = {
  email: string;
};

export function LicenseTransferModal({
  license,
  id,
  onConfirm,
}: {
  license: License;
  id: string;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors, isSubmitted },
    handleSubmit,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const { addToast } = useToast();

  async function onSubmit(data: FormValues) {
    try {
      setIsSubmitting(true);
      await startLicenseTransfer(data.email, license.id);

      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_SUCCESS,
        variant: 'success',
        title: t('TOAST.SUCCESS.TITLE'),
        children: t('PAGES.PROFILE.LICENSES.MY_LICENSES.TRANSFER_LICENSE_MODAL.TOASTS.SUCCESS.MESSAGE', {
          email: data.email,
        }),
      });
      closeModal(id);
      onConfirm();
      setShowAlert(false);
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response?.status === 404) {
          return setError('email', { message: t(`${BASE}.FORM.EMAIL.ERRORS.UNKNOWN_USER`) });
        }
      }

      addToast({
        id: TOAST_ID.LICENSE_TRANSFER_SUCCESS,
        variant: 'danger',
        title: t('TOAST.DANGER.TITLE'),
        children: t(`ALERTS.RETRY.MESSAGE`),
        actions: buildGroupProps(
          <Button prominence="danger" onClick={() => onSubmit(data)}>
            {t(`ALERTS.RETRY.ACTION`)}
          </Button>,
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (isSubmitted && errors && Object.values(errors).some(error => error?.message)) {
      setShowAlert(true);
    }
  }, [isSubmitted, errors]);

  const { closeModal } = useContext(ModalContext);

  return (
    <Dialog id={id} aria-label={t(`${BASE}.A11Y_LABEL`)} modal size="sm">
      <Dialog.Header title={t(`${BASE}.TITLE`)} onClose={() => closeModal(id)} aria-label={t(`${BASE}.CLOSE_LABEL`)} />
      <Dialog.Body>
        {showAlert && <Alert prominence="danger">{t('FORM.ERROR.INVALID')}</Alert>}
        <form id="license-transfer-form" name="license-transfer-form" onSubmit={handleSubmit(onSubmit)}>
          <p>{t(`${BASE}.BODY.P1`, { licenseName: license.name })}</p>
          <p>{t(`${BASE}.BODY.P2`)}</p>
          <TextField
            id="code"
            key="code"
            labelText={t(`${BASE}.FORM.EMAIL.LABEL`)}
            required
            error={!!errors.email}
            errorText={errors.email?.message}
            {...register('email', {
              required: t(`${BASE}.FORM.EMAIL.ERRORS.REQUIRED`),
              pattern: {
                value: /^[\w+.\-_~.']+@([\w-]+\.)+[\w-]{2,12}$/,
                message: t(`${BASE}.FORM.EMAIL.ERRORS.INVALID`),
              },
            })}
          />
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': undefined,
          role: undefined,
          children: (
            <>
              <Button key="cancel" prominence="neutral" onClick={() => closeModal(id)}>
                {t(`${BASE}.ACTIONS.CANCEL`)}
              </Button>
              <Button key="confirm" prominence="success" type="submit" form="license-transfer-form" busy={isSubmitting}>
                {t(`${BASE}.ACTIONS.CONFIRM`)}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
